import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatError, useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatTime } from "@/lib/time";
import { Trash2 } from "lucide-react";

export default function History() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [dinners, setDinners] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null); // dinner obj OR array of ids
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = () => Promise.all([
    api.get("/dinners").then(r=>r.data),
    api.get("/restaurants").then(r=>r.data),
  ]).then(([d,r])=>{setDinners(d);setRestaurants(r); setSelected(new Set());});

  useEffect(() => { load(); }, []);

  const rn = useMemo(() => Object.fromEntries(restaurants.map(r=>[r.id,r.name])), [restaurants]);

  const toggleOne = (id) => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = dinners.length > 0 && selected.size === dinners.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(dinners.map(d=>d.id)));

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      if (Array.isArray(pendingDelete)) {
        const { data } = await api.post("/admin/dinners/bulk-delete", {
          ids: pendingDelete, dry_run: false,
        });
        const c = data.cascaded || {};
        const cascCount = Object.values(c).reduce((a,b)=>a+b, 0);
        toast.success(`Deleted ${data.deleted_dinners} dinner(s)${cascCount ? ` · removed ${cascCount} related record(s)` : ""}`);
      } else {
        const { data } = await api.delete(`/dinners/${pendingDelete.id}`);
        const c = data.cascaded || {};
        const cascCount = Object.values(c).reduce((a,b)=>a+b, 0);
        toast.success(`Dinner deleted${cascCount ? ` · removed ${cascCount} related record(s)` : ""}`);
      }
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setDeleting(false); }
  };

  const deleteLabel = Array.isArray(pendingDelete)
    ? `${pendingDelete.length} selected dinner${pendingDelete.length===1?"":"s"}`
    : (pendingDelete && (rn[pendingDelete.restaurant_id] || "Unassigned"));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Dinner history</div>
          <h1 className="font-serif text-3xl sm:text-4xl">All dinners</h1>
        </div>
        {isAdmin && <div className="text-xs text-muted-foreground">Admin: tick rows to delete several at once. Deleting cascades ratings, RSVPs, and snapshots.</div>}
      </div>

      {isAdmin && dinners.length > 0 && (
        <Card className="p-3 flex items-center justify-between gap-3" data-testid="history-bulk-bar">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
              onCheckedChange={toggleAll}
              data-testid="history-select-all"
            />
            <span>{selected.size === 0 ? "Select all" : `${selected.size} selected`}</span>
          </label>
          <Button
            variant="destructive"
            size="sm"
            disabled={selected.size === 0}
            onClick={()=>setPendingDelete([...selected])}
            data-testid="history-bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-1"/> Delete {selected.size || ""} dinner{selected.size===1?"":"s"}
          </Button>
        </Card>
      )}

      <div className="space-y-3">
        {dinners.length===0 && <div className="text-muted-foreground text-sm">No dinners yet.</div>}
        {dinners.map(d=>(
          <Card key={d.id} className="p-5 hover:border-primary flex justify-between items-center gap-3" data-testid="history-dinner-row">
            {isAdmin && (
              <Checkbox
                checked={selected.has(d.id)}
                onCheckedChange={()=>toggleOne(d.id)}
                onClick={(e)=>e.stopPropagation()}
                data-testid="history-row-checkbox"
              />
            )}
            <Link to={`/dinners/${d.id}`} className="flex-1 min-w-0">
              <div className="font-serif text-xl">{rn[d.restaurant_id] || "Unassigned"}</div>
              <div className="text-xs text-muted-foreground mono mt-1">{d.dinner_date} · {formatTime(d.approved_time || d.recommended_time)}</div>
            </Link>
            <div className="flex items-center gap-3">
              <div className="text-xs text-muted-foreground">{d.participant_member_ids?.length} guests · {d.status.replaceAll("_"," ")}</div>
              {isAdmin && (
                <Button variant="ghost" size="sm"
                        onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setPendingDelete(d); }}
                        data-testid="history-delete-btn"
                        className="text-destructive hover:bg-destructive/10">
                  <Trash2 className="w-4 h-4"/>
                  <span className="ml-1 text-xs hidden sm:inline">Delete</span>
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!pendingDelete} onOpenChange={(o)=>{ if (!o) setPendingDelete(null); }}>
        <DialogContent data-testid="delete-dinner-dialog">
          <DialogHeader>
            <DialogTitle>Delete permanently?</DialogTitle>
            <DialogDescription>
              This removes <strong>{deleteLabel}</strong> along with every rating, RSVP, snapshot, and reservation linked to {Array.isArray(pendingDelete) ? "them" : "it"}.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPendingDelete(null)} disabled={deleting} data-testid="cancel-delete-btn">Cancel</Button>
            <Button onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-btn"
                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
              {deleting ? "Deleting…" : Array.isArray(pendingDelete) ? `Delete ${pendingDelete.length} dinner${pendingDelete.length===1?"":"s"}` : "Delete dinner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
